<template>
  <div
    ref="containerRef"
    class="header-custom-overflow-actions"
    :style="hasMaxWidth ? { maxWidth: `${maxWidth}px` } : undefined">
    <div class="header-custom-actions">
      <zm-button
        v-for="(action, index) in visibleActions"
        :key="action.id ?? action.command ?? index"
        :id="action.id || undefined"
        type="tertiary"
        :icon="action.id === 'discoverProducts' ? ChevronDownV2 : null"
        :class="{ 'is-open': action.id === 'discoverProducts' && isProductsOpen }"
        icon-reverse
        class="header-custom-link"
        @click="onAction(action)">
        {{ action.label }}
      </zm-button>

      <zm-dropdown v-if="overflowActions.length" :z-index="1001" @command="onOverflowCommand">
        <zm-button
          id="headerMoreActionBtn"
          type="tertiary"
          :icon="ChevronDownV2"
          :class="{ 'is-open': !isDiscoverProductsVisible && isProductsOpen }"
          icon-reverse
          class="header-custom-link"
          :aria-label="$t('aria.moreActions')">
          {{ $t('more') }}
        </zm-button>
        <template #dropdown>
          <zm-dropdown-item
            v-for="(action, index) in overflowActions"
            :key="action.id ?? action.command ?? index"
            :command="index">
            {{ action.label }}
          </zm-dropdown-item>
        </template>
      </zm-dropdown>
    </div>

    <div ref="measureRef" class="header-custom-measure" aria-hidden="true">
      <zm-button
        v-for="(action, index) in actions"
        :key="`measure-${action.id ?? action.command ?? index}`"
        type="tertiary"
        :icon="action.id === 'discoverProducts' ? ChevronDownV2 : null"
        icon-reverse
        class="header-custom-link">
        {{ action.label }}
      </zm-button>
      <zm-button
        type="tertiary"
        :icon="ChevronDownV2"
        icon-reverse
        class="header-custom-link"
        :aria-label="$t('aria.moreActions')">
        {{ $t('more') }}
      </zm-button>
    </div>

    <zm-popover
      v-if="showDiscoverProducts"
      ref="popoverRef"
      trigger="manual"
      :disable-click-outside="true"
      class="discover-products-container"
      :aria-label="$t('aria.products')"
      focus-trap
      :z-index="1001"
      :floating-options="{ arrow: false }"
      floating-content-class="products-list-popover"
      placement="bottom"
      @before-hide="onDiscoverBeforeHide">
      <span></span>
      <template #content>
        <Products-list />
      </template>
    </zm-popover>
  </div>
</template>

<script setup>
import { ZmButton, ZmPopover, ZmDropdown, ZmDropdownItem } from '@zoom/zoom-ui-vue3'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, useTemplateRef } from 'vue'
import { ChevronDownV2 } from '@zoom/icons-vue'
import { trackEvent } from '../../../shared/event-tracker'
import ProductsList from './components/discover-products/products-list-new.vue'
import { CONTAINER_PADDING, planOverflow } from './custom-items-layout.js'

const props = defineProps({
  actions: {
    type: Array,
    default: () => []
  },
  maxWidth: Number
})

const containerRef = ref()
const measureRef = ref()
const visibleCount = ref(props.actions.length)

// 0 is a valid constraint (no room left), only null/undefined means "not measured yet".
// Treating 0 as unlimited lets the content expand, which grows the parent and flips
// maxWidth back to > 0, causing an endless resize loop (flicker).
const hasMaxWidth = computed(() => typeof props.maxWidth === 'number' && props.maxWidth >= 0)

const visibleActions = computed(() => props.actions.slice(0, visibleCount.value))
const overflowActions = computed(() => props.actions.slice(visibleCount.value))
const showDiscoverProducts = computed(() =>
  props.actions.some((item) => item.id === 'discoverProducts')
)
const isDiscoverProductsVisible = computed(() =>
  visibleActions.value.some((item) => item.id === 'discoverProducts')
)

const update = async () => {
  await nextTick()

  const container = containerRef.value
  const items = [...(measureRef.value?.children ?? [])]
  const n = props.actions.length

  if (!n) {
    if (visibleCount.value) visibleCount.value = 0
    return
  }

  if (!container || items.length !== n + 1) return

  const slotWidth = hasMaxWidth.value ? props.maxWidth : container.clientWidth
  const nextCount = planOverflow({
    availableWidth: slotWidth - CONTAINER_PADDING,
    itemWidths: items.slice(0, n).map((el) => el.offsetWidth),
    moreWidth: items.at(-1).offsetWidth
  })

  if (visibleCount.value !== nextCount) visibleCount.value = nextCount
}

const popoverRef = useTemplateRef('popoverRef')
const isProductsOpen = ref(false)

const PRODUCTS_POPOVER_CONTENT = '.products-list-popover'
const DISCOVER_TRIGGER_IDS = ['discoverProducts', 'headerMoreActionBtn']

const isInsideDiscoverPopover = (target) => {
  if (!(target instanceof Element)) return false
  if (target.closest(PRODUCTS_POPOVER_CONTENT)) return true
  return DISCOVER_TRIGGER_IDS.some((id) => document.getElementById(id)?.contains(target))
}

const onDiscoverOutsideMouseUp = (event) => {
  if (isInsideDiscoverPopover(event.target)) return
  showDiscoverPopover(false)
}

const attachDiscoverOutsideClick = () => {
  document.removeEventListener('mouseup', onDiscoverOutsideMouseUp)
  document.addEventListener('mouseup', onDiscoverOutsideMouseUp)
}

const detachDiscoverOutsideClick = () => {
  document.removeEventListener('mouseup', onDiscoverOutsideMouseUp)
}

const onDiscoverBeforeHide = () => {
  detachDiscoverOutsideClick()
  isProductsOpen.value = false
}

const showDiscoverPopover = (open) => {
  const targetEl = isDiscoverProductsVisible.value
    ? document.getElementById('discoverProducts')
    : document.getElementById('headerMoreActionBtn')

  if (open) {
    if (targetEl) popoverRef.value?.updateTriggerRef(targetEl)
    popoverRef.value?.open()
    isProductsOpen.value = true
    attachDiscoverOutsideClick()
  } else {
    detachDiscoverOutsideClick()
    popoverRef.value?.close()
    isProductsOpen.value = false
  }
}

const toggleProductsList = () => {
  const nextOpen = !isProductsOpen.value
  showDiscoverPopover(nextOpen)
  trackEvent({
    linkText: `products - ${nextOpen ? 'expand' : 'collapse'}`,
    linkUrl: 'undefined',
    uiLocation: 'universal nav'
  })
}

const onAction = (item) => {
  if (item.id === 'discoverProducts') {
    toggleProductsList()
  } else if (item.id === 'pricing') {
    onPricingClick()
  } else if (item.url) {
    window.open(item.url, item.openIn === '_self' ? '_self' : '_blank')
  }
}

const onOverflowCommand = (index) => {
  const item = overflowActions.value[index]
  if (item) onAction(item)
}

const onPricingClick = () => {
  trackEvent({
    linkText: 'pricing',
    linkUrl: '/pricing',
    uiLocation: 'universal nav'
  })
  setTimeout(() => {
    window.open('/pricing', '_blank')
  }, 100)
}

let observer

onMounted(() => {
  update()
  observer = new ResizeObserver(update)
  observer.observe(containerRef.value)
})

watch(() => props.actions, update, { deep: true })
watch(() => props.maxWidth, update)

onBeforeUnmount(() => {
  observer?.disconnect()
  detachDiscoverOutsideClick()
})
</script>

<style scoped>
.header-custom-overflow-actions {
  min-width: 0;
  overflow: hidden;
  flex: 0 1 auto;
}

.header-custom-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 4px;
  overflow: hidden;
}

.header-custom-actions :deep(.fe-popups-button),
.header-custom-measure :deep(.fe-popups-button) {
  flex: 0 0 auto;
  white-space: nowrap;
}

.header-custom-actions :deep(.fe-popups-button.header-custom-link:focus) {
  color: inherit;
}

.header-custom-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  gap: 12px;
  width: max-content;
}

.header-custom-link.is-open {
  background-color: var(--zoom-color-state-subtle-neutral-hover);
  .fe-popups-icon {
    transform: rotate(180deg);
  }
}
</style>
